/**
 * Badge — pill colorato per ruoli e stati.
 *
 * I "tone" sono mappati a coppie bg + testo usando SOLO token Tailwind
 * dichiarati in `tailwind.config.js` (vedi rule ui-design-system.mdc
 * "MAI inventare classi Tailwind ad-hoc").
 *
 * Per i ruoli usare `roleBadgeTone(role)` in `src/lib/role-display.ts`
 * che restituisce il tone corretto.
 */

import type { ReactNode } from 'react';
import { clsx } from 'clsx';

export type BadgeTone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'primary'
  | 'titolare'
  | 'dietologa'
  | 'segreteria'
  | 'admin';

export interface BadgeProps {
  tone?: BadgeTone;
  /** Compatto per uso inline in liste. */
  size?: 'sm' | 'md';
  className?: string;
  children?: ReactNode;
}

const toneClasses: Record<BadgeTone, string> = {
  neutral: 'bg-surface-container text-text-secondary',
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
  error: 'bg-error/15 text-error',
  info: 'bg-appointment-blue/15 text-appointment-blue',
  primary: 'bg-magenta-soft text-primary',
  titolare: 'bg-success/15 text-success',
  dietologa: 'bg-magenta-soft text-primary',
  segreteria: 'bg-appointment-pink/15 text-appointment-pink',
  admin: 'bg-appointment-gray/15 text-appointment-gray',
};

export function Badge({
  tone = 'neutral',
  size = 'sm',
  className,
  children,
}: BadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full font-semibold tracking-wide',
        size === 'sm'
          ? 'px-2 py-0.5 text-[11px] uppercase'
          : 'px-3 py-1 text-body-sm',
        toneClasses[tone],
        className
      )}
    >
      {children}
    </span>
  );
}
